import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Sparkles, LayoutGrid, ArrowRight, LayoutDashboard } from 'lucide-react'
import {
  SiGooglemarketingplatform,
  SiGoogleads,
  SiMeta,
  SiTiktok,
  SiGoogleanalytics,
  SiHubspot,
  SiMailchimp,
  SiSlack,
  SiShopify,
  SiAmazon,
  SiBigcommerce,
  SiWoocommerce,
  SiEbay,
  SiEtsy,
  SiWalmart,
  SiPaypal,
  SiSquare,
  SiWordpress,
} from 'react-icons/si'

import { changeApplication, seedOnboardDashboard } from 'actions/rootActions'
import { selectRootUser } from 'rootSlice'
import { useConnectionClick } from 'applications/Integrations/components/hooks'
import { Button } from '@/features/shadcn/ui/button'

interface SiteToolsLandingProps {
  /**
   * Whether this viewer can reach the AI Chat page. When false (team member
   * without the `ai` permission), pointing them at /ai would dead-end on
   * Unauthorized, so we lead with the connect path instead.
   */
  aiAllowed?: boolean
  /**
   * Compact strip used inside the real Dashboard (noConnectedStore). Same CTAs,
   * shorter hero — analytics tabs/widgets render below.
   */
  embedded?: boolean
}

const ICON_SIZE = 22
const KlaviyoMark = () => (
  <span className="tw-flex tw-h-[22px] tw-w-[22px] tw-items-center tw-justify-center tw-rounded tw-bg-green-600 tw-text-[11px] tw-font-bold tw-text-white">K</span>
)

// #693: the top non-store tools a store-less user can connect right away (per
// product). `redirect` tools go through the integrations connect flow directly
// (full-page OAuth); `nav` tools need the ConnectionModal (not mounted here), so
// they deep-link into the pre-filtered Integrations page. WordPress and WooCommerce
// are separate cards — search each title so store-less installs open the plugin
// modal and keyed stores open Woo OAuth.
type Tool =
  | { type: 'redirect'; id: string; channel: string; name: string; icon: React.ReactNode }
  | { type: 'nav'; search: string; name: string; icon: React.ReactNode }

// Marketing/analytics tools — shown to no-store ('no_store' path) users.
const MARKETING_TOOLS: Tool[] = [
  { type: 'redirect', id: 'google_marketing', channel: 'Google Marketing', name: 'Google Marketing', icon: <SiGooglemarketingplatform size={ICON_SIZE} color="#4285F4" /> },
  { type: 'redirect', id: 'google_ads', channel: 'Google Ads', name: 'Google Ads', icon: <SiGoogleads size={ICON_SIZE} color="#4285F4" /> },
  { type: 'redirect', id: 'facebook_ads', channel: 'Facebook Ads', name: 'Meta', icon: <SiMeta size={ICON_SIZE} color="#0866FF" /> },
  { type: 'redirect', id: 'tiktok', channel: 'TikTok', name: 'TikTok', icon: <SiTiktok size={ICON_SIZE} color="#000000" /> },
  { type: 'redirect', id: 'google_analytics', channel: 'Google Analytics', name: 'Google Analytics', icon: <SiGoogleanalytics size={ICON_SIZE} color="#E37400" /> },
  { type: 'redirect', id: 'hubspot', channel: 'Hubspot', name: 'HubSpot', icon: <SiHubspot size={ICON_SIZE} color="#FF7A59" /> },
  { type: 'nav', search: 'Klaviyo', name: 'Klaviyo', icon: <KlaviyoMark /> },
  { type: 'nav', search: 'Mailchimp', name: 'Mailchimp', icon: <SiMailchimp size={ICON_SIZE} color="#FFB700" /> },
  { type: 'redirect', id: 'slack', channel: 'Slack', name: 'Slack', icon: <SiSlack size={ICON_SIZE} color="#4A154B" /> },
]

// Store / marketplace / payment integrations — shown to store-intent ('store'
// path) users who skipped connecting. All deep-link to the pre-filtered
// Integrations page (store connects need the shop-URL modal, not mounted here).
const STORE_TOOLS: Tool[] = [
  { type: 'nav', search: 'Shopify', name: 'Shopify', icon: <SiShopify size={ICON_SIZE} color="#95BF47" /> },
  { type: 'nav', search: 'Amazon', name: 'Amazon', icon: <SiAmazon size={ICON_SIZE} color="#FF9900" /> },
  { type: 'nav', search: 'BigCommerce', name: 'BigCommerce', icon: <SiBigcommerce size={ICON_SIZE} color="#121118" /> },
  { type: 'nav', search: 'WordPress', name: 'WordPress', icon: <SiWordpress size={ICON_SIZE} color="#21759B" /> },
  { type: 'nav', search: 'WooCommerce', name: 'WooCommerce', icon: <SiWoocommerce size={ICON_SIZE} color="#96588A" /> },
  { type: 'nav', search: 'eBay', name: 'eBay', icon: <SiEbay size={ICON_SIZE} color="#E53238" /> },
  { type: 'nav', search: 'Etsy', name: 'Etsy', icon: <SiEtsy size={ICON_SIZE} color="#F16521" /> },
  { type: 'nav', search: 'Walmart', name: 'Walmart', icon: <SiWalmart size={ICON_SIZE} color="#0071CE" /> },
  { type: 'nav', search: 'PayPal', name: 'PayPal', icon: <SiPaypal size={ICON_SIZE} color="#003087" /> },
  { type: 'nav', search: 'Square', name: 'Square', icon: <SiSquare size={ICON_SIZE} color="#000000" /> },
]

const ToolCardShell: React.FC<{ icon: React.ReactNode; name: string; onClick: () => void }> = ({ icon, name, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="tw-group tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-4 tw-transition hover:tw-border-indigo-300 hover:tw-shadow-sm"
  >
    <span className="tw-flex tw-h-6 tw-items-center">{icon}</span>
    <span className="tw-text-sm tw-font-medium tw-text-slate-800">{name}</span>
    <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-indigo-600">
      Connect <ArrowRight className="tw-h-3 tw-w-3" />
    </span>
  </button>
)

const RedirectToolCard: React.FC<{ tool: Extract<Tool, { type: 'redirect' }> }> = ({ tool }) => {
  const user = useSelector(selectRootUser)
  const onConnectClick = useConnectionClick({
    user,
    integration: { id: tool.id, channel: tool.channel, title: tool.name, enabled: true },
  })
  return <ToolCardShell icon={tool.icon} name={tool.name} onClick={onConnectClick} />
}

/**
 * Home / connect hub for store-less users (#730, #693).
 * - Full page: used on Traffic/Reports/etc. when there is no connected store.
 * - Embedded: strip at the top of the real Dashboard so seeded widgets can show
 *   below while the merchant still has Connect / AI / Set up my dashboard CTAs.
 */
const SiteToolsLanding: React.FC<SiteToolsLandingProps> = ({ aiAllowed = true, embedded = false }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const currentApp = useSelector((state: any) => state.root.applicationId)
  const user = useSelector(selectRootUser)
  const [seeding, setSeeding] = useState(false)

  const go = (app: string, url: string) => {
    dispatch(changeApplication(currentApp, app))
    navigate(url)
  }

  // #693: store-intent users who skipped connecting get store/marketplace cards;
  // no-store users get marketing/analytics tools.
  const storeIntent = (user as any)?.onboarding_profile?.path === 'store'
  const tools = storeIntent ? STORE_TOOLS : MARKETING_TOOLS
  const profile = (user as any)?.onboarding_profile || {}
  const showSeedCta =
    (user as any)?.onboarding_completed === true && !profile.dashboard_seeded

  const onSeedDashboard = async () => {
    if (seeding) return
    setSeeding(true)
    try {
      const data: any = await dispatch(seedOnboardDashboard() as any)
      const firstTab = data?.seeded_tab_order?.[0]
      if (data?.seeded && firstTab) {
        navigate(`/dashboard?view=tab:${encodeURIComponent(firstTab)}`)
      }
    } finally {
      setSeeding(false)
    }
  }

  // Compact bar for inside the Dashboard: the widgets already show their own
  // "connect this to see data" state, so here we only need small actions — not the
  // full connect hub, which would dominate the seeded tabs.
  if (embedded) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-2 tw-px-4 tw-py-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-600">
          <Sparkles className="tw-h-4 tw-w-4 tw-text-blue-500" />
          <span>
            {storeIntent
              ? 'Connect a store to fill these widgets with live sales data.'
              : 'Connect your marketing & analytics tools to fill these widgets with live data.'}
          </span>
        </div>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          {showSeedCta && (
            <Button size="sm" onClick={onSeedDashboard} disabled={seeding} className="tw-gap-2">
              <LayoutDashboard className="tw-h-4 tw-w-4" />
              {seeding ? 'Setting up…' : 'Set up my dashboard'}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => go('integrations', '/integrations')}
            className="tw-gap-2"
          >
            <LayoutGrid className="tw-h-4 tw-w-4" />
            Connect tools
          </Button>
          {aiAllowed && (
            <Button size="sm" variant="ghost" onClick={() => go('ai', '/ai')} className="tw-gap-2">
              <Sparkles className="tw-h-4 tw-w-4" />
              Ask AI
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={`tw-flex tw-flex-1 tw-justify-center tw-px-4 ${embedded ? 'tw-py-6' : 'tw-py-12'}`}>
      <div className={`tw-w-full ${embedded ? 'tw-max-w-5xl' : 'tw-max-w-3xl'}`}>
        <div className={`tw-text-center ${embedded ? 'tw-mb-5' : 'tw-mb-8'}`}>
          {!embedded && (
            <div className="tw-mx-auto tw-mb-4 tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-50 tw-text-blue-600">
              <Sparkles className="tw-h-7 tw-w-7" />
            </div>
          )}
          <h2 className={`tw-mb-2 tw-font-semibold tw-text-slate-900 ${embedded ? 'tw-text-lg' : 'tw-text-xl'}`}>
            {storeIntent ? 'Connect your store to get started' : 'Connect your tools to get started'}
          </h2>
          <p className="tw-mx-auto tw-max-w-md tw-text-sm tw-leading-relaxed tw-text-slate-500">
            {storeIntent ? (
              <>Link your store, marketplace, or payment processor to unlock sales analytics,
                reports, and inventory — then let the AI assistant do the heavy lifting.</>
            ) : (
              <>Link your marketing and analytics tools to unlock traffic, ad, and revenue insights —
                then let the AI assistant do the heavy lifting.</>
            )}
          </p>
        </div>

        <div className={`tw-grid tw-gap-3 ${embedded ? 'tw-grid-cols-3 sm:tw-grid-cols-4 lg:tw-grid-cols-5' : 'tw-grid-cols-2 sm:tw-grid-cols-3'}`}>
          {tools.map((tool) =>
            tool.type === 'redirect' ? (
              <RedirectToolCard key={tool.name} tool={tool} />
            ) : (
              <ToolCardShell
                key={tool.name}
                icon={tool.icon}
                name={tool.name}
                onClick={() => navigate(`/integrations?search=${encodeURIComponent(tool.search)}`)}
              />
            )
          )}
        </div>

        <div className="tw-mt-8 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 sm:tw-flex-row tw-flex-wrap">
          {aiAllowed && (
            <Button onClick={() => go('ai', '/ai')} className="tw-gap-2">
              <Sparkles className="tw-h-4 tw-w-4" />
              Open AI Assistant
              <ArrowRight className="tw-h-4 tw-w-4" />
            </Button>
          )}
          <Button variant="outline" onClick={() => go('integrations', '/integrations')} className="tw-gap-2">
            <LayoutGrid className="tw-h-4 tw-w-4" />
            Connect more
          </Button>
          {showSeedCta && (
            <Button
              variant={embedded ? 'default' : 'outline'}
              onClick={onSeedDashboard}
              disabled={seeding}
              className="tw-gap-2"
            >
              <LayoutDashboard className="tw-h-4 tw-w-4" />
              {seeding ? 'Setting up…' : 'Set up my dashboard'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default SiteToolsLanding
